import {
  Grid,
  IconButton,
  Typography,
  Box,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { CustomizedBox } from "../../../components/Custom/CustomizedBox";
import CustomizedTextField from "../../../components/Custom/CustomizedTextField";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import CustomizedSelect from "../../../components/Custom/CustomizedSelect";
import CustomizedComboBox from "../../../components/Custom/CustomizedComboBox";
import SalesService from "../../../services/Sales";
import { dateToUnix } from "../../../utils/date-converter";
import { useAuth } from "../../../hooks/use-auth";
import InventoryService from "../../../services/Inventory";
import PurchaseService from "../../../services/Purchase";
import { CustomizedTooltip } from "../../../components/Custom/CustomizedTooltip";
import ManufactureService from "../../../services/Manufacture";
import { useParams } from "react-router-dom";
import { useDebounce } from "../../../hooks/use-debounce";
import SearchIcon from "@mui/icons-material/Search";
import { useSnackbar } from "notistack";

const defaultOptions = {
  data: [],
  type: "",
};

const IssueDetail = ({
  isCreate,
  control,
  setValue,
  getValues,
  errors,
  disabled,
  isLoading,
  watchWarehouse,
  generateDocumentId,
  replace,
  reset,
  resetFieldBarcode,
  isLoadingRefDocument,
  setIsLoadingRefDocument,
}) => {
  const [referenceDocumentOptions, setReferenceDocumentOptions] =
    useState(defaultOptions);
  const { t } = useTranslation();
  const { allLocation } = useSelector((state) => state.location);
  const { user } = useAuth();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [currentReferenceDocumentId, setCurrentReferenceDocumentId] =
    useState("");

  const warehouseOption = allLocation?.map((location) => ({
    id: location.document_id,
    label: location.thai_name,
    value: location.document_id,
  }));

  const watchType = useWatch({ control, name: "type" });
  const currentWarehouse = useWatch({
    control,
    name: "source_warehouse_document_id",
  });

  const mapWarehouseIdToName = () => {
    const findId = allLocation?.find(
      (location) => location.document_id === currentWarehouse
    );
    return findId ? findId.thai_name : "";
  };

  const typeOption = [
    { label: "ขาย", value: "ขาย" },
    { label: "ขายออนไลน์", value: "ขายออนไลน์" },
    { label: "ผลิตวัตถุดิบ", value: "ผลิตวัตถุดิบ" },
    { label: "ผลิตแปรรูป", value: "ผลิตแปรรูป" },
    { label: "ผลิตแปรรูป - PT", value: "ผลิตแปรรูป - PT" },
    { label: "ส่งคืน", value: "ส่งคืน" },
    { label: "สินค้าเสีย", value: "สินค้าเสีย" },
    { label: "อื่นๆ", value: "อื่นๆ" },
  ];

  const prevLoadedRef = useRef("");

  const onCurrentReferenceDocumentInputChange = useDebounce((value) => {
    setCurrentReferenceDocumentId(value);
  }, 500);

  const fetchDocument = useCallback(async () => {
    const type = getValues("type");
    setIsLoadingOptions(true);
    setReferenceDocumentOptions({ ...defaultOptions });

    let data = null;

    const baseFilter = {
      startRow: 0,
      endRow: 10,
      filterModel: {
        render_status: {
          values: ["ขาย", "ขายออนไลน์", "ส่งคืน"].includes(type)
            ? ["approved", "finished"]
            : ["finished", "inProgress", "pendingManu"],
          filterType: "set",
        },
        document_id: currentReferenceDocumentId
          ? {
              filterType: "text",
              type: "contains",
              filter: currentReferenceDocumentId,
              mode: "insensitive",
            }
          : undefined,
      },
      sortModel: [
        {
          sort: "desc",
          colId: "created_date",
        },
      ],
    };

    // if (["ขาย", "ขายออนไลน์"].includes(type)) {
    //   const { results } = await SalesService.getAllSalesOrderIds(baseFilter);
    //   data = results;
    // } else
    if (["ผลิตวัตถุดิบ", "ผลิตแปรรูป", "ผลิตแปรรูป - PT"].includes(type)) {
      const { results } = await ManufactureService.getAllManufactureOrderIds(
        baseFilter
      );
      data = results;
    } else if (["ส่งคืน"].includes(type)) {
      const { results } = await PurchaseService.getAllPurchaseReturnIds(
        baseFilter
      );
      data = results;
    }

    setReferenceDocumentOptions({ data: data || [], type });
    setIsLoadingOptions(false);
  }, [currentReferenceDocumentId, getValues]);

  useEffect(() => {
    if (!id && watchType) {
      fetchDocument();
    }
  }, [fetchDocument, watchType, currentReferenceDocumentId, id]);

  const resetValue = useCallback(
    (onlyList) => {
      if (isCreate) {
        if (!onlyList) setValue("reference_document_id", "");
        setValue("goods_issue_list", []);
      }
    },
    [isCreate, setValue]
  );

  const checkTypeIfSameDoc = useCallback(async () => {
    const type = getValues("type");
    const refDoc = getValues("reference_document_id");

    setCurrentReferenceDocumentId("");
    if (
      (refDoc.slice(0, 2) === "SO" &&
        type !== "ขาย" &&
        type !== "ขายออนไลน์") ||
      type === "ขาย" ||
      type === "ขายออนไลน์"
    ) {
      resetValue();
    } else if (
      refDoc.slice(0, 2) === "MO" &&
      type !== "ผลิตวัตถุดิบ" &&
      type !== "ผลิตแปรรูป" &&
      type !== "ผลิตแปรรูป - PT"
    ) {
      resetValue();
    } else if (refDoc.slice(0, 2) === "SR" && type !== "ส่งคืน") {
      resetValue();
    } else if (type === "สินค้าเสีย" || type === "อื่นๆ") {
      resetValue();
    } else if ((type !== "สินค้าเสีย" || type !== "อื่นๆ") && refDoc === "") {
      resetValue(true);
    }
  }, [getValues, resetValue]);

  const selectedDocumentHandler = async (field, newValue) => {
    const currValue = getValues();
    resetFieldBarcode("barcode");

    if (newValue?.value) {
      const isDifferentDoc = currValue.reference_document_id !== newValue.value;

      if (isDifferentDoc) {
        reset({
          ...currValue,
          reference_document_id: newValue.value,
          goods_issue_list: [],
        });
      }

      if (prevLoadedRef.current === newValue.value) {
        setIsLoadingRefDocument(false);
        return;
      }

      let findItemFromRef;
      const type = currValue.type;

      // if (["ขาย", "ขายออนไลน์"].includes(type)) {
      //   const res = await SalesService.getSalesOrderById({
      //     document_id: newValue.value,
      //   });
      //   findItemFromRef = {
      //     ...res,
      //     item_list: res.item_list.map((item) => ({
      //       ...item,
      //       document_id: item.item_document_id,
      //       name: item.item_name,
      //       description: item.item_description,
      //       quantity: item.qty,
      //       uom: item.uom,
      //     })),
      //   };
      // }
      // else
      if (["ผลิตวัตถุดิบ", "ผลิตแปรรูป", "ผลิตแปรรูป - PT"].includes(type)) {
        const res = await ManufactureService.getManufactureOrderById(
          newValue.value
        );
        findItemFromRef = {
          ...res,
          item_list: res.ingredient_list.map((item) => ({
            ...item,
            document_id: item.item_document_id,
            name: item.item_name,
            description: item.item_description,
            quantity: item.qty,
            uom: { document_id: item.uom, name: item.uom },
          })),
        };
      } else if (["ส่งคืน"].includes(type)) {
        const res = await PurchaseService.getPurchaseReturnById({
          document_id: newValue.value,
        });
        findItemFromRef = {
          ...res,
          item_list: res.item_list.map((item) => ({
            ...item,
            document_id: item.item_document_id,
            name: item.item_name,
            description: item.item_description,
            quantity: item.qty,
            uom: item.uom,
          })),
        };
      }

      if (findItemFromRef) {
        const { results } = await InventoryService.getAllSEAggrid({
          startRow: 0,
          endRow: 999999,
          filterModel: {
            inventory_object_item: {
              filterType: "object",
              filter: {
                goods_issue: {
                  filterType: "object",
                  filter: {
                    reference_document_id: {
                      filterType: "text",
                      filter: newValue.value,
                      type: "contains",
                    },
                  },
                },
              },
            },
            is_active: {
              filterType: "boolean",
              type: "equals",
              filter: "true",
            },
          },
          sortModel: [],
        });

        const formatValue = findItemFromRef.item_list.map((item) => {
          const matchedEntries = results.filter(
            (result) => result.trace_entry_list[0]?.item_uid === item?.uid
          );

          const alreadyPostedQty = matchedEntries.reduce(
            (prev, curr) => prev + Math.abs(curr.posted_quantity),
            0
          );

          return {
            initial_quantity: item.quantity,
            posted_quantity: item.quantity,
            posted_value: 0,
            already_posted_quantity: alreadyPostedQty,
            stock_entry_list: {
              destination_warehouse: "",
              entry_type: "",
              is_active: true,
              item,
              posted_date: dateToUnix(new Date()),
              posted_quantity: item.quantity,
              posted_value: 0,
              reference_document_id: "",
              reference_document_type: "issue",
              source_warehouse_document_id: watchWarehouse,
              trace_entry_list: [],
              created_by: user,
              uom: item.uom,
              item_uid: item.uid,
            },
          };
        });

        replace(formatValue);
      }

      prevLoadedRef.current = newValue.value;
    } else {
      reset({
        ...currValue,
        reference_document_id: "",
        goods_issue_list: [],
      });
    }

    setIsLoadingRefDocument(false);
  };

  const searchSalesOrderHandler = async (documentId) => {
    setIsLoadingRefDocument(true);
    try {
      const res = await SalesService.getSalesOrderById({
        document_id: documentId,
      });

      const isIssueAble = ["approved", "finished"].includes(res.render_status);

      if (isIssueAble) {
        const findItemFromRef = {
          ...res,
          item_list: res.item_list.map((item) => ({
            ...item,
            document_id: item.item_document_id,
            name: item.item_name,
            description: item.item_description,
            quantity: item.qty,
            uom: item.uom,
          })),
        };

        if (findItemFromRef) {
          const { results } = await InventoryService.getAllSEAggrid({
            startRow: 0,
            endRow: 999999,
            filterModel: {
              inventory_object_item: {
                filterType: "object",
                filter: {
                  goods_issue: {
                    filterType: "object",
                    filter: {
                      reference_document_id: {
                        filterType: "text",
                        filter: documentId,
                        type: "contains",
                      },
                    },
                  },
                },
              },
              is_active: {
                filterType: "boolean",
                type: "equals",
                filter: "true",
              },
            },
            sortModel: [],
          });

          const formatValue = findItemFromRef.item_list.map((item) => {
            const matchedEntries = results.filter(
              (result) => result.trace_entry_list[0]?.item_uid === item?.uid
            );

            const alreadyPostedQty = matchedEntries.reduce(
              (prev, curr) => prev + Math.abs(curr.posted_quantity),
              0
            );

            return {
              initial_quantity: item.quantity,
              posted_quantity: item.quantity,
              posted_value: 0,
              already_posted_quantity: alreadyPostedQty,
              stock_entry_list: {
                destination_warehouse: "",
                entry_type: "",
                is_active: true,
                item,
                posted_date: dateToUnix(new Date()),
                posted_quantity: item.quantity,
                posted_value: 0,
                reference_document_id: "",
                reference_document_type: "issue",
                source_warehouse_document_id: watchWarehouse,
                trace_entry_list: [],
                created_by: user,
                uom: item.uom,
                item_uid: item.uid,
              },
            };
          });

          const allAlreadyPosted = formatValue.every(
            (f) => f.already_posted_quantity >= f.initial_quantity
          );

          if (allAlreadyPosted) {
            enqueueSnackbar("ไม่มีรายการรอนำออก", {
              variant: "error",
            });
            replace([]);
          } else {
            const reference_document_id = getValues("reference_document_id");
            if (reference_document_id) {
              replace(formatValue);
            }
          }
        }
      } else {
        enqueueSnackbar(
          "เอกสารต้องอยู่ในสถานะ อนุมัติแล้ว/เสร็จสิ้น แล้วเท่านั้น",
          {
            variant: "error",
          }
        );
        replace([]);
      }
      setIsLoadingRefDocument(false);
    } catch (err) {
      const error = err.response.errors[0].message;
      if (error.includes("No record was found for a query")) {
        enqueueSnackbar("ไม่พบเลขที่เอกสาร", {
          variant: "error",
        });
      }
      replace([]);
      setIsLoadingRefDocument(false);
    }
  };

  return (
    <CustomizedBox margin={0}>
      <Typography sx={{ fontWeight: 700, mb: 3 }}>
        {t("description")}
      </Typography>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={disabled ? 12 : 10} md={6} lg={4}>
          <Controller
            control={control}
            name="document_id"
            render={({ field }) => (
              <CustomizedTextField
                {...field}
                label={t("inventory.issue.documentId")}
                error={Boolean(errors.document_id)}
                helperText={errors.document_id?.message}
                required
                disabled={!isCreate || isLoading}
              />
            )}
          />
        </Grid>
        {!disabled && (
          <Grid item xs={2}>
            <Box sx={{ ml: -1 }}>
              <CustomizedTooltip title="เรียกเลขที่เอกสารใหม่">
                <IconButton onClick={generateDocumentId}>
                  <RestartAltOutlinedIcon />
                </IconButton>
              </CustomizedTooltip>
            </Box>
          </Grid>
        )}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6} lg={4}>
          <Controller
            control={control}
            name="type"
            render={({ field }) => (
              <CustomizedSelect
                {...field}
                options={typeOption}
                label={t("inventory.issue.issueType")}
                onChange={(e) => {
                  field.onChange(e);
                  checkTypeIfSameDoc();
                }}
                disabled={!isCreate || isLoading}
                required
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          {["สินค้าเสีย", "อื่นๆ"].includes(watchType) ? (
            <Controller
              control={control}
              name="reference_document_id"
              render={({ field }) => (
                <CustomizedTextField
                  {...field}
                  label={t("inventory.referenceNo")}
                  error={Boolean(errors.reference_document_id)}
                  helperText={errors.reference_document_id?.message}
                  disabled
                />
              )}
            />
          ) : ["ขาย", "ขายออนไลน์"].includes(watchType) ? (
            <Controller
              control={control}
              name="reference_document_id"
              render={({ field }) => (
                <CustomizedTextField
                  {...field}
                  label={t("inventory.referenceNo")}
                  error={Boolean(errors.reference_document_id)}
                  helperText={errors.reference_document_id?.message}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {isLoadingRefDocument && (
                          <CircularProgress size={20} color="inherit" />
                        )}
                        <IconButton
                          onClick={async () =>
                            await searchSalesOrderHandler(field.value)
                          }
                          disabled={
                            !isCreate || isLoading || isLoadingRefDocument
                          }
                          size="small"
                        >
                          <SearchIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  disabled={!isCreate || isLoading}
                  required
                  onKeyDown={async (e) => {
                    if (
                      e.key === "Enter" &&
                      !e.shiftKey &&
                      !isLoadingRefDocument
                    ) {
                      e.preventDefault();
                      await searchSalesOrderHandler(field.value);
                    }
                  }}
                />
              )}
            />
          ) : (
            <Controller
              control={control}
              name="reference_document_id"
              render={({ field }) => (
                <CustomizedComboBox
                  {...field}
                  loading={isLoadingOptions}
                  error={Boolean(errors.reference_document_id)}
                  helperText={errors.reference_document_id?.message}
                  options={referenceDocumentOptions.data.map((doc) => ({
                    id: doc.id,
                    value: doc.document_id,
                    label: doc.document_id,
                  }))}
                  label={t("inventory.referenceNo")}
                  onChange={(_, newValue) => {
                    setIsLoadingRefDocument(true);
                    selectedDocumentHandler(field, newValue);
                  }}
                  onInputChange={(_, value) => {
                    onCurrentReferenceDocumentInputChange(value);
                  }}
                  disabled={!isCreate || isLoading}
                  required
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Controller
            control={control}
            name="source_warehouse_document_id"
            render={({ field }) => (
              <CustomizedComboBox
                options={warehouseOption}
                label={t("inventory.warehouse")}
                onChange={(_, newValue) =>
                  field.onChange(newValue ? newValue.value : "")
                }
                error={Boolean(errors.source_warehouse_document_id)}
                helperText={errors.source_warehouse_document_id?.message}
                value={mapWarehouseIdToName()}
                disabled={!isCreate || isLoading}
                required
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Controller
            control={control}
            name="remark"
            render={({ field }) => (
              <CustomizedTextField
                {...field}
                label={t("inventory.remark")}
                error={Boolean(errors.remark)}
                helperText={errors.remark?.message}
                disabled={!isCreate || isLoading}
                required
              />
            )}
          />
        </Grid>
      </Grid>
    </CustomizedBox>
  );
};

export default IssueDetail;
